import {engineContext} from './context.js';

/* ZooInvest Hype / Narrative Phase Engine v0.1.0-alpha
 * Engine-only. No layout changes.
 * Purpose: distinguish early discovery / institutional participation / media adoption /
 * social-media saturation / broad hype from verified fundamental progress.
 * Research compression only. Never auto-trades. Thresholds are PROVISIONAL / UNCALIBRATED.
 */
(function(root,factory){
  const api=factory();
  if(root) root.ZooInvestHypeNarrativeEngine=api;
})(engineContext,function(){
  'use strict';
  const VERSION='0.1.0-alpha';
  const CALIBRATED=false;
  const CONFIG=Object.freeze({
    calibrated:false,
    thresholds:'PROVISIONAL / UNCALIBRATED',
    minimumEvidenceForStrongConclusion:3
  });

  const num=v=>typeof v==='number'&&Number.isFinite(v);
  const clamp=(v,a=0,b=10)=>Math.max(a,Math.min(b,v));
  const norm=v=>String(v??'').trim().toUpperCase();
  const freeze=o=>Object.freeze(o);
  const avg=xs=>xs.length?xs.reduce((a,b)=>a+b,0)/xs.length:null;

  function normalizeEvidence(e={}){
    return freeze({
      type:norm(e.type||'UNKNOWN'),
      verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(norm(e.verification))?norm(e.verification):'UNVERIFIED',
      sourceClass:norm(e.sourceClass||'UNKNOWN'),
      independent:e.independent===true,
      timestamp:e.timestamp?String(e.timestamp):null
    });
  }

  function evidenceQuality(evidence=[]){
    const rows=evidence.map(normalizeEvidence);
    const verified=rows.filter(x=>x.verification==='VERIFIED').length;
    const independentVerified=rows.filter(x=>x.verification==='VERIFIED'&&x.independent).length;
    return freeze({
      total:rows.length,
      verified,
      independentVerified,
      strong:verified>=CONFIG.minimumEvidenceForStrongConclusion && independentVerified>=2,
      rows:freeze(rows)
    });
  }

  function narrativeSignals(input={}){
    const media=num(input.mediaIntensity)?clamp(input.mediaIntensity):null;
    const social=num(input.socialIntensity)?clamp(input.socialIntensity):null;
    const youtube=num(input.youtubeIntensity)?clamp(input.youtubeIntensity):null;
    const search=num(input.searchTrend)?clamp(input.searchTrend):null;
    const retail=num(input.retailParticipation)?clamp(input.retailParticipation):null;
    const price=num(input.priceAcceleration)?clamp(input.priceAcceleration):null;
    const valuation=num(input.valuationExtension)?clamp(input.valuationExtension):null;
    const vals=[media,social,youtube,search,retail].filter(num);
    return freeze({
      media,social,youtube,search,retail,priceAcceleration:price,valuationExtension:valuation,
      attentionScore:vals.length?Number(avg(vals).toFixed(2)):null
    });
  }

  function fundamentalProgress(input={}){
    const dims={
      revenueAcceleration:input.revenueAcceleration,
      marginProgress:input.marginProgress,
      cashFlowProgress:input.cashFlowProgress,
      demandEvidence:input.demandEvidence,
      customerValidation:input.customerValidation,
      executionProgress:input.executionProgress
    };
    const vals=Object.values(dims).filter(num).map(v=>clamp(v));
    return freeze({
      score:vals.length?Number(avg(vals).toFixed(2)):null,
      coverage:Number((vals.length/Object.keys(dims).length).toFixed(3)),
      missing:Object.entries(dims).filter(([,v])=>!num(v)).map(([k])=>k),
      dimensions:freeze({...dims})
    });
  }

  function capitalPhase(input={}){
    const inst=num(input.institutionalFlow)?clamp(input.institutionalFlow):null;
    const sector=num(input.sectorFlow)?clamp(input.sectorFlow):null;
    const breadth=num(input.marketBreadth)?clamp(input.marketBreadth):null;
    const vals=[inst,sector,breadth].filter(num);
    return freeze({
      score:vals.length?Number(avg(vals).toFixed(2)):null,
      institutionalFlow:inst,
      sectorFlow:sector,
      marketBreadth:breadth
    });
  }

  function classifyPhase(input={},evidence=[]){
    const n=narrativeSignals(input), f=fundamentalProgress(input), c=capitalPhase(input), eq=evidenceQuality(evidence);
    const media=n.media??0, social=Math.max(n.social??0,n.youtube??0), retail=n.retail??0;
    const inst=c.institutionalFlow??0, att=n.attentionScore??0;
    let phase=0,label='UNKNOWN',reason='INSUFFICIENT_INPUT';

    if(inst>=6 && media<4 && social<4){
      phase=2; label='INSTITUTIONAL_DISCOVERY'; reason='INSTITUTIONAL_FLOW_PRECEDES_BROAD_ATTENTION';
    } else if(media>=5 && social<6){
      phase=3; label='MEDIA_ADOPTION'; reason='MEDIA_ATTENTION_IS_BROADENING';
    } else if(social>=6 && retail>=5){
      phase=4; label='SOCIAL_SATURATION'; reason='SOCIAL_AND_RETAIL_ATTENTION_DOMINATE';
    } else if(att>=7.5 && social>=8 && retail>=7){
      phase=5; label='BROAD_HYPE'; reason='ATTENTION_IS_WIDESPREAD_AND_REFLEXIVE';
    } else if((f.score??0)>=5 && media<4 && social<4 && retail<4){
      phase=1; label='EARLY_DISCOVERY'; reason='FUNDAMENTALS_AHEAD_OF_BROAD_ATTENTION';
    }

    // Phase 5 takes precedence when extreme saturation is present.
    if(att>=7.5 && social>=8 && retail>=7){
      phase=5; label='BROAD_HYPE'; reason='ATTENTION_IS_WIDESPREAD_AND_REFLEXIVE';
    }

    let confidence='LOW';
    if(eq.strong) confidence='HIGH';
    else if(eq.verified>=1) confidence='MEDIUM';

    // Strong phase language cannot be high-confidence on weak evidence.
    if(!eq.strong && confidence==='HIGH') confidence='MEDIUM';

    return freeze({
      phase,label,reason,confidence,
      narrative:n,fundamentals:f,capital:c,evidence:eq,
      companyQualityEffect:0,
      tradeSignal:false,
      action:phase>=4?'REVIEW':'WATCH'
    });
  }

  function hypeFundamentalGap(input={},evidence=[]){
    const p=classifyPhase(input,evidence);
    const hype=p.narrative.attentionScore;
    const fundamental=p.fundamentals.score;
    if(!num(hype)||!num(fundamental)){
      return freeze({status:'OPEN',gap:null,label:'UNKNOWN',reason:'MISSING_ATTENTION_OR_FUNDAMENTALS',tradeSignal:false});
    }
    const gap=Number((hype-fundamental).toFixed(2));
    let label='BALANCED';
    if(gap>=4) label='NARRATIVE_FAR_AHEAD';
    else if(gap>=2) label='NARRATIVE_AHEAD';
    else if(gap<=-3) label='FUNDAMENTALS_AHEAD';
    return freeze({
      status:'AVAILABLE',gap,label,
      priceContextOnly:true,
      companyQualityEffect:0,
      tradeSignal:false,
      note:'The gap is a review flag, not a valuation or trading conclusion.'
    });
  }

  function expertClaimChallenge(claim={}){
    const source=norm(claim.sourceClass||'UNKNOWN');
    const hasPrimary=claim.primaryEvidence===true;
    const verified=norm(claim.verification)==='VERIFIED';
    const quantified=claim.quantified===true;
    const falsifiable=claim.falsifiable===true;
    const counterCase=claim.counterCaseIncluded===true;
    const flags=[];
    if(['INFLUENCER','SOCIAL','MEDIA'].includes(source)&&!hasPrimary) flags.push('SECONDARY_CLAIM_WITHOUT_PRIMARY_EVIDENCE');
    if(!verified) flags.push('CLAIM_NOT_VERIFIED');
    if(!quantified) flags.push('CLAIM_NOT_QUANTIFIED');
    if(!falsifiable) flags.push('NO_FALSIFIABLE_TEST');
    if(!counterCase) flags.push('NO_COUNTER_CASE');

    return freeze({
      state:flags.length>=3?'INVESTIGATE':flags.length?'REVIEW':'SUPPORTED_FOR_RESEARCH',
      flags:freeze(flags),
      authorityBonus:0,
      companyQualityEffect:0,
      tradeSignal:false,
      questions:freeze([
        'What primary evidence supports the claim?',
        'What number or milestone would confirm it?',
        'What would prove the claim wrong?',
        'What is the strongest credible counter-case?'
      ])
    });
  }

  function decisionBridge(input={},evidence=[]){
    const phase=classifyPhase(input,evidence);
    const gap=hypeFundamentalGap(input,evidence);
    const flags=[];
    if(phase.phase>=4) flags.push('HYPE_PHASE_ELEVATED');
    if(gap.label==='NARRATIVE_AHEAD'||gap.label==='NARRATIVE_FAR_AHEAD') flags.push('NARRATIVE_OUTRUNNING_FUNDAMENTALS');
    if((phase.narrative.valuationExtension??0)>=8) flags.push('VALUATION_EXTENSION_HIGH');
    return freeze({
      status:flags.length?'REVIEW':'OPEN',
      flags:freeze(flags),
      destination:'WHY_WRONG / TIMING',
      companyQualityEffect:0,
      entryGateOverride:false,
      tradeSignal:false,
      note:'Narrative/hype context may increase review pressure in WHY WRONG? / TIMING, but cannot rewrite Company Quality or bypass Entry Gate.'
    });
  }

  function selfTest(){
    const tests=[], t=(name,pass)=>tests.push({name,pass:!!pass});
    const ev=[
      {verification:'VERIFIED',independent:true},
      {verification:'VERIFIED',independent:true},
      {verification:'VERIFIED',independent:false}
    ];

    let r=classifyPhase({
      institutionalFlow:8,sectorFlow:7,marketBreadth:6,
      mediaIntensity:2,socialIntensity:2,youtubeIntensity:1,retailParticipation:2,
      revenueAcceleration:7,marginProgress:6,cashFlowProgress:5,demandEvidence:8,customerValidation:7,executionProgress:6
    },ev);
    t('Institutional participation can precede media/social hype',r.phase===2&&r.label==='INSTITUTIONAL_DISCOVERY');

    r=classifyPhase({
      mediaIntensity:9,socialIntensity:9,youtubeIntensity:10,searchTrend:9,retailParticipation:9,
      institutionalFlow:7,revenueAcceleration:4,marginProgress:3,cashFlowProgress:2,demandEvidence:4,customerValidation:4,executionProgress:4
    },ev);
    t('Broad saturation reaches hype phase 5',r.phase===5&&r.label==='BROAD_HYPE');
    t('Hype classification never becomes a trade signal',r.tradeSignal===false);
    t('Hype classification never rewrites Company Quality',r.companyQualityEffect===0);

    const gap=hypeFundamentalGap({
      mediaIntensity:9,socialIntensity:9,youtubeIntensity:9,searchTrend:9,retailParticipation:9,
      revenueAcceleration:3,marginProgress:3,cashFlowProgress:3,demandEvidence:3,customerValidation:3,executionProgress:3
    },ev);
    t('Narrative far ahead of fundamentals is flagged',gap.label==='NARRATIVE_FAR_AHEAD');

    const claim=expertClaimChallenge({sourceClass:'INFLUENCER',verification:'UNVERIFIED',primaryEvidence:false,quantified:false,falsifiable:false,counterCaseIncluded:false});
    t('Influencer claim without evidence is investigate',claim.state==='INVESTIGATE');
    t('Expert prestige has zero authority bonus',claim.authorityBonus===0);

    const weak=classifyPhase({
      mediaIntensity:9,socialIntensity:9,youtubeIntensity:9,retailParticipation:9
    },[{verification:'UNVERIFIED',independent:false}]);
    t('Weak evidence cannot create high confidence',weak.confidence!=='HIGH');

    const bridge=decisionBridge({
      mediaIntensity:9,socialIntensity:9,youtubeIntensity:9,searchTrend:9,retailParticipation:9,valuationExtension:9,
      revenueAcceleration:3,marginProgress:3,cashFlowProgress:3,demandEvidence:3,customerValidation:3,executionProgress:3
    },ev);
    t('Bridge routes hype to WHY WRONG / TIMING only',bridge.destination==='WHY_WRONG / TIMING'&&bridge.companyQualityEffect===0&&bridge.entryGateOverride===false);

    const early=classifyPhase({
      mediaIntensity:1,socialIntensity:1,youtubeIntensity:1,retailParticipation:1,
      revenueAcceleration:8,marginProgress:7,cashFlowProgress:7,demandEvidence:8,customerValidation:7,executionProgress:8,
      institutionalFlow:2
    },ev);
    t('Fundamentals can lead attention in early discovery',early.phase===1&&early.label==='EARLY_DISCOVERY');

    return freeze({passed:tests.filter(x=>x.pass).length,total:tests.length,tests});
  }

  const API={VERSION,CALIBRATED,CONFIG,normalizeEvidence,evidenceQuality,narrativeSignals,fundamentalProgress,capitalPhase,classifyPhase,hypeFundamentalGap,expertClaimChallenge,decisionBridge,selfTest};
  try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
  return freeze(API);
});
